import { useQueryGetExamPaperStatus } from "@/apis/exam_progress/getExamPaperStatus";
import { useMutationSubmitModule } from "@/apis/exam_progress/submitModule";
import React, { useState, useRef, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import ReviewListPopover from "./ReviewListPopover";
import { IS_DEVELOPMENT } from "@/utils/env";

interface ExamFooterProps {
  studentName: string;
  currentQuestionIdx: number;
  totalQuestions: number;
  disablePrev: boolean;
  disableNext: boolean;
  sectionTitle?: string;
  onQuestionSelect?: (idx: number) => void;
  answeredQuestions?: number[]; // 0-based indices
  forReviewQuestions?: number[]; // 0-based indices
  onReviewClick?: () => void;
  onReviewNext?: () => void | Promise<void>;
}

const ExamFooter: React.FC<ExamFooterProps> = ({
  studentName,
  currentQuestionIdx,
  totalQuestions,
  disablePrev,
  sectionTitle = "Section",
  onQuestionSelect,
  answeredQuestions = [],
  forReviewQuestions = [],
  onReviewClick,
  onReviewNext,
}) => {
  const { id, exam_paper_id } = useParams<{
    id: string;
    exam_paper_id: string;
  }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const isReview = searchParams.get("question") === "review";
  const [popoverOpen, setPopoverOpen] = useState(false);
  const isLastQuestion =
    searchParams.get("question") === String(totalQuestions);
  const { data: progressStatus } = useQueryGetExamPaperStatus(
    id || "",
    exam_paper_id || ""
  );

  const { mutate: submitModule, isPending: isSubmitting } =
    useMutationSubmitModule();

  // Next 버튼 클릭 시
  const handleNext = () => {
    if (isReview && onReviewNext) {
      onReviewNext();
      return;
    }
    if (!progressStatus || progressStatus.status !== "in_progress") return;
    if (isReview) {
      if (!id || !exam_paper_id) return;
      submitModule(
        { progressId: id, examPaperId: exam_paper_id },
        {
          onSuccess: () => {
            // 다음 섹션으로 이동 후 1번 문제로 이동
            setSearchParams({ question: "1" });
            // 상태 refetch
            if (typeof window !== "undefined") {
              window.location.reload(); // 임시: refetch가 없으면 강제 새로고침
            }
          },
        }
      );
    } else if (isLastQuestion) {
      setSearchParams({ question: "review" });
    } else {
      setSearchParams({ question: String(currentQuestionIdx + 2) });
    }
  };
  // Prev 버튼 클릭 시
  const handlePrev = () => {
    if (isReview) {
      // 리뷰 페이지에서는 마지막 문제로 이동
      setSearchParams({ question: String(totalQuestions) });
    } else if (currentQuestionIdx > 0) {
      setSearchParams({ question: String(currentQuestionIdx) });
    }
  };

  const popoverRef = useRef<HTMLDivElement>(null);

  // 외부 클릭 시 팝오버 닫기
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        setPopoverOpen(false);
      }
    }
    if (popoverOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [popoverOpen]);

  return (
    <div className="flex items-center justify-between px-8 py-3 bg-[#e6edf8] custom-dashed-border-top relative">
      <div className="text-sm text-gray-800 font-bold font-sans">
        {studentName}
      </div>
      <div className="absolute left-1/2 -translate-x-1/2">
        {!isReview && (
          <>
            <button
              className="bg-black text-white font-bold rounded-md px-6 py-2 text-base shadow text-center select-none cursor-pointer"
              style={{ minWidth: 160 }}
              onClick={() => setPopoverOpen((v) => !v)}
            >
              Question {currentQuestionIdx + 1} of {totalQuestions}
            </button>
            <ReviewListPopover
              isOpen={popoverOpen}
              currentQuestionIdx={currentQuestionIdx}
              onClose={() => setPopoverOpen(false)}
              sectionTitle={sectionTitle}
              totalQuestions={totalQuestions}
              answeredQuestions={answeredQuestions}
              forReviewQuestions={forReviewQuestions}
              onQuestionSelect={(idx) => onQuestionSelect?.(idx)}
              onReviewClick={onReviewClick}
            />
          </>
        )}
      </div>
      <div className="flex items-center gap-3">
        {/* SEAN YOON's SOLUTION 워터마크 */}
        <div 
          className="text-gray-400 text-xs font-bold select-none pointer-events-none hidden lg:flex"
          style={{ opacity: 0.6 }}
        >
          SEAN YOON's SOLUTION
        </div>
        {/* Back 버튼: 첫 문제에서는 숨김 */}
        {!disablePrev && (
          <button
            className="bg-[#3451d6] hover:bg-[#22336b] text-white rounded-full px-6 py-2 font-bold text-base shadow transition-all duration-150 ease-in-out transform hover:scale-[1.02] active:scale-[0.95] cursor-pointer"
            onClick={handlePrev}
          >
            Back
          </button>
        )}
        {(!isReview || IS_DEVELOPMENT) && (
          <button
            className="bg-[#3451d6] hover:bg-[#22336b] text-white rounded-full px-6 py-2 font-bold text-base shadow transition-all duration-150 ease-in-out transform hover:scale-[1.02] active:scale-[0.95] cursor-pointer"
            disabled={isSubmitting}
            onClick={handleNext}
          >
            Next
          </button>
        )}
      </div>
    </div>
  );
};

export default ExamFooter;
